import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import {
  Search as SearchIcon,
  Filter,
  Plus,
  Clock,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  PanelLeftClose,
  PanelRightClose,
  LayoutGrid,
  PanelLeft,
  Square,
  GripVertical,
  RotateCcw,
  Map as MapIcon,
  BarChart3,
  Gauge,
  Sparkles,
  Send,
  ArrowDownWideNarrow,
  type LucideIcon,
} from 'lucide-react'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  closestCenter,
  getFirstCollision,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import CabinaTopbar from '@/components/cabina/CabinaTopbar'
import LeadSlideOver from '@/components/cabina/LeadSlideOver'
import KanbanBoard from '@/components/cabina/KanbanBoard'
import { usePipeline } from '@/hooks/usePipeline'
import BusinessMap from '@/components/cabina/BusinessMap'
import {
  ZoneMapWidget,
  ResultsDistWidget,
  SelectionQualityWidget,
  ZoneIntelPremiumWidget,
} from '@/components/cabina/searchWidgets'
import {
  getAccountQuota,
  type AccountQuota,
  type InboxFilters,
  type InboxItem,
} from '@/services/listingsService'
import { opportunityScore } from '@/lib/opportunityScore'
import { DEMO_QUOTA, DEMO_PIPELINE } from '@/pages/dashboardDemo'

const IS_PREVIEW =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('preview')
import {
  useSearchAndInbox,
  useInboxMapItems,
  useAllJobs,
  NewSearchForm,
  ActiveJobBanner,
  FiltersPanel,
  FiltersInlineChips,
  InboxTable,
  ColumnPickerButton,
  ExportButtons,
  Paginator,
  JobsPastList,
  JobsHistory,
  JobKpis,
  InboxEmptyState,
  InboxErrorBanner,
  ConfirmModal,
  countActiveFilters,
  hasActiveFilters,
  jobLabel,
  type UseSearchAndInbox,
} from './searchShared'
import { useLeadView } from '@/store/leadView'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'

/**
 * Búsqueda — escritorio de widgets configurable (T-018 Paso 1, DEC-015).
 *
 * Parte de la disposición V1 "Triple panel" y la generaliza en un
 * escritorio donde el usuario organiza la información a su gusto:
 *
 *   • Selector de MODO DE ZONAS arriba:
 *       - 3 zonas   → lateral izquierdo + tabla central + lateral derecho.
 *       - 2 zonas   → un único lateral (izq o der a elección) + tabla.
 *       - sin laterales → solo la tabla a todo el ancho, con los controles
 *         compactos arriba (estilo "command bar" del mockup V2).
 *   • Los bloques de los laterales son WIDGETS independientes y
 *     reposicionables (filtros, nueva extracción, estado del job, búsquedas
 *     pasadas). Cada widget se mueve entre laterales y se reordena de dos
 *     formas equivalentes:
 *       - Arrastrar y soltar con el ratón/teclado (Paso 2, dnd-kit) — se
 *         agarra por el "grip" de la cabecera; sensores de puntero + teclado
 *         con ARIA activos para accesibilidad.
 *       - Controles simples en la cabecera (←/→ entre laterales, ↑/↓ orden)
 *         que se MANTIENEN como alternativa accesible al arrastre.
 *     El DnD solo aplica en los modos con laterales (3 zonas y 2 zonas).
 *   • Al cambiar de modo NINGÚN widget se pierde: en 2 zonas todos los
 *     widgets caen en el lateral que queda; en "sin laterales" se ocultan
 *     pero se RECUERDAN y reaparecen en su panel y orden al volver.
 *   • COLAPSO de los laterales: un lateral colapsado se reduce a un raíl
 *     muy compacto con los iconos de sus widgets (detalle distintivo).
 *   • Toda la configuración (modo, lado del lateral, qué widget en qué
 *     panel, orden, colapsos) se persiste en localStorage. Las columnas
 *     visibles ya se persisten aparte vía el hook (clave -cols-v1).
 *
 * Regla dura T-018: NO se pierde nada de la funcionalidad previa — el
 * formulario de extracción (categorías + zona + estimar + extraer + modal
 * de confirmación DEC-024 capa 3), el banner de job con polling, la bandeja
 * con todos sus filtros, la tabla de 24 columnas + ColumnPicker, la
 * paginación y los 3 exports (CSV/XLSX/JSON) siguen operativos en todos los
 * modos.
 */

// ============================================================================
// Modelo de widgets
// ============================================================================

type WidgetId =
  | 'filters'
  | 'new-search'
  | 'job-status'
  | 'past-jobs'
  | 'zone-map'
  | 'zone-dist'
  | 'sel-quality'
  | 'zone-intel'
type PanelSide = 'left' | 'right'
type ZoneMode = 'three' | 'two' | 'none'

/** Estado persistido de un widget: su panel "hogar" (usado en 3 zonas) y
 *  un orden GLOBAL entero que determina la secuencia tanto dentro de su
 *  panel (3 zonas) como en el lateral único combinado (2 zonas). */
interface WidgetState {
  id: WidgetId
  panel: PanelSide
  order: number
}

interface WorkspaceConfig {
  zoneMode: ZoneMode
  /** Lado del lateral único en modo "2 zonas". */
  singleSide: PanelSide
  widgets: WidgetState[]
  leftCollapsed: boolean
  rightCollapsed: boolean
}

type IconType = LucideIcon

/** Definición declarativa de cada widget. Para añadir un widget nuevo en el
 *  futuro basta con sumar una entrada aquí + su placement por defecto en
 *  DEFAULT_PLACEMENT; el motor de disposición se encarga del resto. */
interface WidgetDef {
  id: WidgetId
  label: string
  Icon: IconType
  render: (state: UseSearchAndInbox) => ReactNode
}

const WIDGET_REGISTRY: WidgetDef[] = [
  {
    id: 'filters',
    label: 'Filtros',
    Icon: Filter,
    render: state => (
      <FiltersPanel
        filters={state.filters}
        jobs={state.jobs}
        onApply={state.applyFilters}
        onClear={state.clearFilters}
        bare
      />
    ),
  },
  {
    id: 'job-status',
    label: 'Estado actual',
    Icon: Clock,
    render: state => <JobKpis activeJob={state.activeJob} jobs={state.jobs} />,
  },
  {
    id: 'past-jobs',
    label: 'Mis búsquedas',
    Icon: SearchIcon,
    render: state => (
      <JobsPastList
        jobs={state.jobs}
        activeJobId={state.filters.job_id}
        onSelect={id => state.applyFilters({ ...state.filters, job_id: id })}
        onClearSelection={() =>
          state.applyFilters({ ...state.filters, job_id: undefined })
        }
      />
    ),
  },
  {
    id: 'new-search',
    label: 'Nueva extracción',
    Icon: Plus,
    render: state => <NewSearchForm state={state} compact />,
  },
  {
    id: 'zone-map',
    label: 'Mapa de la zona',
    Icon: MapIcon,
    render: state => <ZoneMapWidget items={state.items} filters={state.filters} />,
  },
  {
    id: 'zone-dist',
    label: 'Distribución de resultados',
    Icon: BarChart3,
    render: state => <ResultsDistWidget items={state.items} />,
  },
  {
    id: 'sel-quality',
    label: 'Calidad de la selección',
    Icon: Gauge,
    render: state => <SelectionQualityWidget items={state.items} />,
  },
  {
    id: 'zone-intel',
    label: 'Inteligencia de zona',
    Icon: Sparkles,
    render: () => <ZoneIntelPremiumWidget />,
  },
]

const DEFAULT_PLACEMENT: Record<WidgetId, { panel: PanelSide; order: number }> =
  {
    'new-search': { panel: 'left', order: 0 },
    filters: { panel: 'left', order: 1 },
    'zone-map': { panel: 'right', order: 2 },
    'zone-dist': { panel: 'right', order: 3 },
    'sel-quality': { panel: 'right', order: 4 },
    'job-status': { panel: 'right', order: 5 },
    'past-jobs': { panel: 'right', order: 6 },
    'zone-intel': { panel: 'right', order: 7 },
  }

const WORKSPACE_STORAGE_KEY = 'mapeodata-search-workspace-v1'

/** Pestaña activa de Búsqueda, persistida (T-027): al refrescar se restaura la
 *  última pestaña en vez de volver siempre a "Tabla de leads". */
/** Etiquetas legibles del orden activo de la tabla (T-029), para el subtítulo. */
const INBOX_SORT_LABELS: Record<string, string> = {
  title: 'nombre',
  category_primary: 'categoría',
  city: 'ciudad',
  rating_value: 'valoración',
  rating_votes_count: 'reseñas',
  is_claimed: 'reclamado',
  first_extracted_at: 'fecha',
  jobs_count: 'búsquedas',
}

const VIEW_STORAGE_KEY = 'mapeodata-search-view-v1'
type SearchView = 'table' | 'kanban' | 'map' | 'history'
function loadView(): SearchView {
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY)
    if (v === 'table' || v === 'kanban' || v === 'map' || v === 'history') return v
  } catch {
    /* ignore */
  }
  return 'table'
}

const PANEL_WIDTH = { left: '264px', right: '312px', single: '300px' } as const
const RAIL_WIDTH = '48px'

// ----------------------------------------------------------------------------
// Persistencia + normalización
// ----------------------------------------------------------------------------

function defaultWidgets(): WidgetState[] {
  return WIDGET_REGISTRY.map(def => ({
    id: def.id,
    panel: DEFAULT_PLACEMENT[def.id].panel,
    order: DEFAULT_PLACEMENT[def.id].order,
  }))
}

/** Garantiza que la lista de widgets contiene EXACTAMENTE los del registro:
 *  conserva los conocidos (con su placement guardado), descarta los
 *  desconocidos y añade los que falten con su placement por defecto. Esto
 *  hace la persistencia robusta cuando el catálogo de widgets crezca. */
function normalizeWidgets(input: unknown): WidgetState[] {
  const seen = new Map<WidgetId, WidgetState>()
  if (Array.isArray(input)) {
    for (const raw of input) {
      if (!raw || typeof raw !== 'object') continue
      const id = (raw as { id?: unknown }).id
      const def = WIDGET_REGISTRY.find(d => d.id === id)
      if (!def || seen.has(def.id)) continue
      const panelRaw = (raw as { panel?: unknown }).panel
      const orderRaw = (raw as { order?: unknown }).order
      seen.set(def.id, {
        id: def.id,
        panel: panelRaw === 'right' ? 'right' : 'left',
        order:
          typeof orderRaw === 'number'
            ? orderRaw
            : DEFAULT_PLACEMENT[def.id].order,
      })
    }
  }
  for (const def of WIDGET_REGISTRY) {
    if (!seen.has(def.id)) {
      seen.set(def.id, {
        id: def.id,
        panel: DEFAULT_PLACEMENT[def.id].panel,
        order: DEFAULT_PLACEMENT[def.id].order,
      })
    }
  }
  return WIDGET_REGISTRY.map(def => seen.get(def.id)!)
}

function loadConfig(): WorkspaceConfig {
  // Preview (bucle de fidelidad): `?layout=none|two|three` fuerza el modo para
  // las capturas (p. ej. `none` deja la bandeja —y su buscador— a todo el ancho).
  if (IS_PREVIEW) {
    const layout = new URLSearchParams(window.location.search).get('layout')
    if (layout === 'none' || layout === 'two' || layout === 'three') {
      return {
        zoneMode: layout,
        singleSide: 'left',
        widgets: defaultWidgets(),
        leftCollapsed: false,
        rightCollapsed: false,
      }
    }
  }
  try {
    const raw = localStorage.getItem(WORKSPACE_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<WorkspaceConfig>
      const zoneMode: ZoneMode =
        parsed.zoneMode === 'two' || parsed.zoneMode === 'none'
          ? parsed.zoneMode
          : 'three'
      return {
        zoneMode,
        singleSide: parsed.singleSide === 'right' ? 'right' : 'left',
        widgets: normalizeWidgets(parsed.widgets),
        leftCollapsed: !!parsed.leftCollapsed,
        rightCollapsed: !!parsed.rightCollapsed,
      }
    }
  } catch {
    // ignore — caemos a defaults
  }
  return {
    zoneMode: 'three',
    singleSide: 'left',
    widgets: defaultWidgets(),
    leftCollapsed: false,
    rightCollapsed: false,
  }
}

// ----------------------------------------------------------------------------
// Operaciones sobre el modelo de widgets
// ----------------------------------------------------------------------------

function widgetsInPanel(widgets: WidgetState[], side: PanelSide): WidgetState[] {
  return widgets.filter(w => w.panel === side).sort((a, b) => a.order - b.order)
}

function allWidgetsSorted(widgets: WidgetState[]): WidgetState[] {
  return [...widgets].sort((a, b) => a.order - b.order)
}

/** Reordena un widget intercambiando su `order` con el vecino. `scope`
 *  'panel' (3 zonas: dentro de su lateral) o 'all' (2 zonas: lateral único). */
function reorderWidget(
  widgets: WidgetState[],
  id: WidgetId,
  direction: 'up' | 'down',
  scope: 'panel' | 'all',
): WidgetState[] {
  const target = widgets.find(w => w.id === id)
  if (!target) return widgets
  const sequence =
    scope === 'all'
      ? allWidgetsSorted(widgets)
      : widgetsInPanel(widgets, target.panel)
  const idx = sequence.findIndex(w => w.id === id)
  const swapIdx = direction === 'up' ? idx - 1 : idx + 1
  if (swapIdx < 0 || swapIdx >= sequence.length) return widgets
  const a = sequence[idx]
  const b = sequence[swapIdx]
  return widgets.map(w =>
    w.id === a.id
      ? { ...w, order: b.order }
      : w.id === b.id
        ? { ...w, order: a.order }
        : w,
  )
}

/** Mueve un widget al otro lateral, colocándolo al final de ese lateral. */
function moveWidgetToPanel(
  widgets: WidgetState[],
  id: WidgetId,
  target: PanelSide,
): WidgetState[] {
  const maxOrder = widgets.reduce((m, w) => Math.max(m, w.order), 0)
  return widgets.map(w =>
    w.id === id ? { ...w, panel: target, order: maxOrder + 1 } : w,
  )
}

// ----------------------------------------------------------------------------
// Drag & drop (dnd-kit) — IDs de las zonas droppables + resolución del drop
// ----------------------------------------------------------------------------

/** IDs de las áreas droppables de cada lateral (distintas de las IDs de los
 *  widgets, que son sus propios `WidgetId`). Permiten soltar en el espacio
 *  vacío de un lateral o detectar el lateral destino al cruzar. */
const DROP_ID: Record<PanelSide, string> = {
  left: 'droppable-panel-left',
  right: 'droppable-panel-right',
}

function panelOfDropId(id: string): PanelSide | null {
  if (id === DROP_ID.left) return 'left'
  if (id === DROP_ID.right) return 'right'
  return null
}

/**
 * Transfiere `activeId` al lateral `side` en la posición `index` (usado en
 * vivo por `onDragOver`, patrón MultipleContainers de dnd-kit). Reasigna
 * `panel` + `order` global de forma coherente (left primero, luego right).
 * Es la pieza que faltaba: sin mover el widget al SortableContext destino
 * DURANTE el arrastre, dnd-kit no deja que el item cruce de lateral.
 */
function moveWidgetToContainerAt(
  widgets: WidgetState[],
  activeId: WidgetId,
  side: PanelSide,
  index: number,
): WidgetState[] {
  let leftIds = widgetsInPanel(widgets, 'left').map(w => w.id)
  let rightIds = widgetsInPanel(widgets, 'right').map(w => w.id)
  leftIds = leftIds.filter(id => id !== activeId)
  rightIds = rightIds.filter(id => id !== activeId)
  const target = side === 'left' ? leftIds : rightIds
  const at = Math.max(0, Math.min(index, target.length))
  target.splice(at, 0, activeId)
  const placement = new Map<WidgetId, { panel: PanelSide; order: number }>()
  leftIds.forEach((id, i) => placement.set(id, { panel: 'left', order: i }))
  rightIds.forEach((id, i) =>
    placement.set(id, { panel: 'right', order: leftIds.length + i }),
  )
  return widgets.map(w => {
    const p = placement.get(w.id)
    return p ? { ...w, panel: p.panel, order: p.order } : w
  })
}

/**
 * Calcula el nuevo array de widgets tras soltar `activeId` sobre `overId`.
 * `overId` puede ser otro widget (reorden relativo) o el área droppable de un
 * lateral (soltar al final / en lateral vacío). Reasigna el `order` global y,
 * en 3 zonas, el `panel` de cada widget para mantener el estado coherente.
 *
 * - 2 zonas: solo reordena el lateral único; NO toca el `panel` de los
 *   widgets (así al volver a 3 zonas reaparecen en su lateral original).
 * - 3 zonas: permite reordenar dentro de un lateral y cruzar de lateral.
 */
function applyDrop(
  widgets: WidgetState[],
  activeId: WidgetId,
  overId: string,
  mode: ZoneMode,
): WidgetState[] {
  if (mode === 'none') return widgets
  if (activeId === overId) return widgets
  const active = widgets.find(w => w.id === activeId)
  if (!active) return widgets

  if (mode === 'two') {
    const ids = allWidgetsSorted(widgets).map(w => w.id)
    const oldIndex = ids.indexOf(activeId)
    const newIndex = panelOfDropId(overId)
      ? ids.length - 1
      : ids.indexOf(overId as WidgetId)
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return widgets
    const reordered = arrayMove(ids, oldIndex, newIndex)
    const orderOf = new Map(reordered.map((id, i) => [id, i]))
    return widgets.map(w => ({ ...w, order: orderOf.get(w.id) ?? w.order }))
  }

  // mode === 'three'
  const dropPanel = panelOfDropId(overId)
  const overPanel: PanelSide | undefined = dropPanel
    ? dropPanel
    : widgets.find(w => w.id === overId)?.panel
  if (!overPanel) return widgets

  let leftIds = widgetsInPanel(widgets, 'left').map(w => w.id)
  let rightIds = widgetsInPanel(widgets, 'right').map(w => w.id)

  if (active.panel === overPanel && !dropPanel) {
    // Reorden dentro del mismo lateral.
    const list = active.panel === 'left' ? leftIds : rightIds
    const oldIndex = list.indexOf(activeId)
    const newIndex = list.indexOf(overId as WidgetId)
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return widgets
    const reordered = arrayMove(list, oldIndex, newIndex)
    if (active.panel === 'left') leftIds = reordered
    else rightIds = reordered
  } else {
    // Cruce de lateral (o soltar en el área del lateral destino).
    leftIds = leftIds.filter(id => id !== activeId)
    rightIds = rightIds.filter(id => id !== activeId)
    let target = overPanel === 'left' ? leftIds : rightIds
    let insertAt = dropPanel ? target.length : target.indexOf(overId as WidgetId)
    if (insertAt < 0) insertAt = target.length
    target = [...target.slice(0, insertAt), activeId, ...target.slice(insertAt)]
    if (overPanel === 'left') leftIds = target
    else rightIds = target
  }

  const placement = new Map<WidgetId, { panel: PanelSide; order: number }>()
  leftIds.forEach((id, i) => placement.set(id, { panel: 'left', order: i }))
  rightIds.forEach((id, i) =>
    placement.set(id, { panel: 'right', order: leftIds.length + i }),
  )
  return widgets.map(w => {
    const p = placement.get(w.id)
    return p ? { ...w, panel: p.panel, order: p.order } : w
  })
}

// ============================================================================
// Componente principal
// ============================================================================

export default function Search() {
  const state = useSearchAndInbox()
  const [config, setConfig] = useState<WorkspaceConfig>(loadConfig)

  // Capa de inteligencia / cabina (no altera la lógica del hook).
  const [view, setView] = useState<SearchView>(loadView)
  const [showExtract, setShowExtract] = useState(false)
  const [sortByOpp, setSortByOpp] = useState(true)
  const [selectedLead, setSelectedLead] = useState<InboxItem | null>(null)
  const [quota, setQuota] = useState<AccountQuota | null>(null)

  // Pipeline / kanban real (backend propio). Compartido por el kanban, la ficha
  // y la acción masiva de la tabla.
  const pipeline = usePipeline()
  const addToPipelineNuevo = useCallback((ids: string[]) => pipeline.add(ids, 'nuevo'), [pipeline])

  // El mapa muestra TODOS los leads del usuario (dataset completo con los filtros
  // activos), no solo la página de la tabla. Se carga al abrir la pestaña Mapa.
  const mapItems = useInboxMapItems(state.filters, view === 'map', state.items)

  // El historial completo de búsquedas (todas las páginas) se carga al abrir la
  // pestaña Historial. El selector lateral sigue usando state.jobs (≤200).
  const allJobs = useAllJobs(view === 'history')

  // Acciones del historial: ver los leads de una búsqueda (filtra + Tabla) o
  // repetir la búsqueda (pre-rellena las categorías; la zona la elige el user).
  const viewJobLeads = (jobId: string) => {
    state.applyFilters({ ...state.filters, job_id: jobId })
    setView('table')
  }
  const resumeJob = (job: typeof allJobs.jobs[number]) => {
    const cats = job.categories ?? job.query?.categories ?? []
    if (cats.length > 0) state.setSelectedCategoryIds(cats)
    setView('table')
  }

  useEffect(() => {
    let cancelled = false
    getAccountQuota()
      .then(q => { if (!cancelled) setQuota(q) })
      .catch(() => { if (!cancelled && IS_PREVIEW) setQuota(DEMO_QUOTA) })
    return () => { cancelled = true }
  }, [])

  // Lead solicitado desde el command palette global (⌘K) — abre su ficha. Se
  // pasa el item completo, así que funciona aunque no esté en la página actual.
  const pendingLead = useLeadView(s => s.pendingLead)
  const consumeLead = useLeadView(s => s.consumeLead)
  useEffect(() => {
    if (!pendingLead) return
    setSelectedLead(pendingLead)
    consumeLead()
  }, [pendingLead, consumeLead])

  // Preview (bucle de fidelidad): abrir vista/ficha vía URL para capturar.
  const previewOpened = useRef(false)
  useEffect(() => {
    if (!IS_PREVIEW || previewOpened.current || state.items.length === 0) return
    const params = new URLSearchParams(window.location.search)
    if (params.has('kanban') || params.get('view') === 'kanban') setView('kanban')
    if (params.has('map') || params.get('view') === 'map') setView('map')
    if (params.has('history') || params.get('view') === 'history') setView('history')
    if (params.has('lead')) {
      // `?lead=pipeline` abre un lead que SÍ está en el pipeline para ver el
      // seguimiento completo (próximo paso + composer + timeline). Por defecto
      // abre el top por Opportunity Score (que en el demo NO está en pipeline →
      // muestra el CTA "Añadir al pipeline").
      const leadParam = params.get('lead')
      if (leadParam === 'pipeline' && DEMO_PIPELINE.length > 0) {
        const target = DEMO_PIPELINE[0]
        const match = state.items.find(i => i.place_id === target.place_id) ?? target
        setSelectedLead(match)
      } else if (leadParam && state.items.some(i => i.place_id === leadParam)) {
        // `?lead=<place_id>` abre un lead concreto (útil para capturas de
        // fidelidad: p.ej. un negocio CON web vs SIN web).
        setSelectedLead(state.items.find(i => i.place_id === leadParam)!)
      } else {
        const top = [...state.items].sort(
          (a, b) => opportunityScore(b).score - opportunityScore(a).score,
        )[0]
        setSelectedLead(top)
      }
    }
    if (params.has('extract')) setShowExtract(true)
    previewOpened.current = true
  }, [state.items])

  useEffect(() => {
    try {
      localStorage.setItem(WORKSPACE_STORAGE_KEY, JSON.stringify(config))
    } catch {
      // ignore
    }
  }, [config])

  // Persistir la pestaña activa (T-027) para restaurarla al refrescar.
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, view)
    } catch {
      // ignore
    }
  }, [view])

  // Cerrar el modal de nueva extracción (T-028) con Escape.
  useEffect(() => {
    if (!showExtract) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowExtract(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [showExtract])

  const { zoneMode, singleSide, widgets, leftCollapsed, rightCollapsed } = config

  const setZoneMode = (m: ZoneMode) => setConfig(c => ({ ...c, zoneMode: m }))
  const setSingleSide = (s: PanelSide) =>
    setConfig(c => ({ ...c, singleSide: s }))
  const toggleCollapsed = (side: PanelSide) =>
    setConfig(c =>
      side === 'left'
        ? { ...c, leftCollapsed: !c.leftCollapsed }
        : { ...c, rightCollapsed: !c.rightCollapsed },
    )
  const moveWidget = (id: WidgetId, direction: 'up' | 'down') =>
    setConfig(c => ({
      ...c,
      widgets: reorderWidget(
        c.widgets,
        id,
        direction,
        c.zoneMode === 'two' ? 'all' : 'panel',
      ),
    }))
  const switchWidgetPanel = (id: WidgetId, current: PanelSide) =>
    setConfig(c => ({
      ...c,
      widgets: moveWidgetToPanel(c.widgets, id, current === 'left' ? 'right' : 'left'),
    }))
  const resetWorkspace = () =>
    setConfig({
      zoneMode: 'three',
      singleSide: 'left',
      widgets: defaultWidgets(),
      leftCollapsed: false,
      rightCollapsed: false,
    })

  // ── Drag & drop (dnd-kit, patrón MultipleContainers) ───────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const [activeWidgetId, setActiveWidgetId] = useState<WidgetId | null>(null)
  const activeDef = activeWidgetId
    ? WIDGET_REGISTRY.find(d => d.id === activeWidgetId)
    : null

  // Refs del patrón MultipleContainers: estabilizan la detección de colisión
  // al cruzar de contenedor y permiten restaurar si se cancela el arrastre.
  const lastOverId = useRef<string | number | null>(null)
  const recentlyMovedToNewContainer = useRef(false)
  const clonedWidgets = useRef<WidgetState[] | null>(null)

  // Reset del flag tras el render que sigue a un cruce de contenedor.
  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false
    })
  }, [widgets])

  /**
   * Detección de colisión del ejemplo oficial MultipleContainers: primero
   * `pointerWithin`/`rectIntersection` para localizar el contenedor bajo el
   * cursor; si ese contenedor tiene items, `closestCenter` entre sus items
   * para elegir el destino exacto. `lastOverId` da estabilidad mientras se
   * cruza (evita parpadeos / pérdida del `over`).
   */
  const collisionDetectionStrategy: CollisionDetection = useCallback(
    args => {
      const pointerIntersections = pointerWithin(args)
      const intersections =
        pointerIntersections.length > 0
          ? pointerIntersections
          : rectIntersection(args)
      let overId = getFirstCollision(intersections, 'id')
      if (overId != null) {
        const overSide = panelOfDropId(String(overId))
        if (overSide) {
          const containerItems =
            zoneMode === 'two'
              ? allWidgetsSorted(widgets).map(w => w.id)
              : widgetsInPanel(widgets, overSide).map(w => w.id)
          if (containerItems.length > 0) {
            const closest = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                c =>
                  c.id !== overId &&
                  containerItems.includes(String(c.id) as WidgetId),
              ),
            })[0]?.id
            if (closest != null) overId = closest
          }
        }
        lastOverId.current = overId
        return [{ id: overId }]
      }
      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeWidgetId
      }
      return lastOverId.current != null ? [{ id: lastOverId.current }] : []
    },
    [activeWidgetId, widgets, zoneMode],
  )

  const handleDragStart = (e: DragStartEvent) => {
    setActiveWidgetId(e.active.id as WidgetId)
    clonedWidgets.current = widgets
  }

  /**
   * onDragOver — TRANSFIERE el widget al lateral destino EN VIVO (solo en 3
   * zonas, único modo con dos contenedores). Sin esto, el widget pertenece al
   * SortableContext de origen y dnd-kit no lo deja cruzar. El orden final lo
   * fija onDragEnd.
   */
  const handleDragOver = (e: DragOverEvent) => {
    if (zoneMode !== 'three') return
    const { active, over } = e
    if (!over) return
    const activeId = active.id as WidgetId
    const overId = String(over.id)
    const overSideRaw = panelOfDropId(overId)
    const isContainer = overSideRaw != null
    const overTop = over.rect.top
    const overHeight = over.rect.height
    const translatedTop = active.rect.current.translated?.top ?? null

    setConfig(c => {
      const activeWidget = c.widgets.find(w => w.id === activeId)
      if (!activeWidget) return c
      const overContainer = isContainer
        ? overSideRaw
        : c.widgets.find(w => w.id === overId)?.panel
      if (!overContainer) return c
      if (activeWidget.panel === overContainer) return c // mismo lateral → onDragEnd
      const overItems = widgetsInPanel(c.widgets, overContainer).map(w => w.id)
      let newIndex: number
      if (isContainer) {
        newIndex = overItems.length
      } else {
        const overIndex = overItems.indexOf(overId as WidgetId)
        const isBelow =
          translatedTop != null && translatedTop > overTop + overHeight / 2
        newIndex =
          overIndex >= 0 ? overIndex + (isBelow ? 1 : 0) : overItems.length
      }
      recentlyMovedToNewContainer.current = true
      return {
        ...c,
        widgets: moveWidgetToContainerAt(
          c.widgets,
          activeId,
          overContainer,
          newIndex,
        ),
      }
    })
  }

  const handleDragCancel = () => {
    if (clonedWidgets.current) {
      const restored = clonedWidgets.current
      setConfig(c => ({ ...c, widgets: restored }))
    }
    setActiveWidgetId(null)
    clonedWidgets.current = null
  }

  // onDragEnd fija el orden final dentro del lateral destino (y persiste vía el
  // useEffect de config). En cruce, onDragOver ya movió el widget al lateral
  // destino, así que applyDrop resuelve un reorden dentro de ese lateral;
  // applyDrop también cubre el cruce directo como fallback (p. ej. teclado).
  const handleDragEnd = (e: DragEndEvent) => {
    setActiveWidgetId(null)
    clonedWidgets.current = null
    const overId = e.over?.id
    if (overId == null) return
    const activeId = e.active.id as WidgetId
    setConfig(c => ({
      ...c,
      widgets: applyDrop(c.widgets, activeId, String(overId), c.zoneMode),
    }))
  }

  /** Renderiza la lista de widgets de un lateral (área droppable + contexto
   *  sortable). `list` ya viene ordenada; `dropId` identifica el lateral. */
  const renderWidgetCards = (list: WidgetState[], dropId: string): ReactNode => (
    <SortableWidgetList
      list={list}
      dropId={dropId}
      showPanelMove={zoneMode === 'three'}
      state={state}
      onMoveUp={id => moveWidget(id, 'up')}
      onMoveDown={id => moveWidget(id, 'down')}
      onSwitchPanel={(id, panel) => switchWidgetPanel(id, panel)}
    />
  )

  const showActiveBanner =
    state.activeJob &&
    (state.activeJob.status === 'pending' || state.activeJob.status === 'running')

  return (
    <div className="cabina-root">
      <CabinaTopbar />
      <div className="wrap">
        <div className="page-head">
          <div>
            <div className="crumb">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M3 7h18M3 12h18M3 17h18" /></svg>
              Taller de extracción
            </div>
            <h1>Búsqueda</h1>
            <p>
              Tu escritorio de leads con su capa de inteligencia — mueve los widgets,
              colapsa los laterales o quédate solo con la tabla.
            </p>
          </div>
        </div>

        <div className="view-tabs">
          <button
            type="button"
            className={'vt' + (view === 'table' ? ' active' : '')}
            onClick={() => setView('table')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 5h18M3 12h18M3 19h18M9 5v14" /></svg>
            Tabla de leads
          </button>
          <button
            type="button"
            className={'vt' + (view === 'kanban' ? ' active' : '')}
            onClick={() => setView('kanban')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="5" height="16" rx="1" /><rect x="10" y="4" width="5" height="11" rx="1" /><rect x="17" y="4" width="5" height="14" rx="1" /></svg>
            Kanban
          </button>
          <button
            type="button"
            className={'vt' + (view === 'map' ? ' active' : '')}
            onClick={() => setView('map')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 20l-5 2V6l5-2 6 2 5-2v16l-5 2-6-2z" /><path d="M9 4v16M15 6v16" /></svg>
            Mapa
          </button>
          <button
            type="button"
            className={'vt' + (view === 'history' ? ' active' : '')}
            onClick={() => setView('history')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 109-9 9 9 0 00-9 9z" /><path d="M3 4v5h5" /><path d="M12 7v5l3 2" /></svg>
            Historial
          </button>
          <button
            type="button"
            className="btn btn-accent vt-extract"
            onClick={() => setShowExtract(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
            Nueva extracción
          </button>
        </div>

        {showActiveBanner && state.activeJob && (
          <div style={{ marginBottom: 14 }}>
            <ActiveJobBanner job={state.activeJob} />
          </div>
        )}

        {view === 'kanban' ? (
          <KanbanBoard
            cards={pipeline.cards}
            loading={pipeline.loading && !pipeline.loaded}
            error={pipeline.error}
            orderBy={pipeline.orderBy}
            onOrderBy={pipeline.setOrderBy}
            onOpenLead={setSelectedLead}
            onMove={pipeline.move}
            onRemove={pipeline.remove}
            onAddLeads={() => setView('table')}
          />
        ) : view === 'map' ? (
          <div className="map-tab">
            <div className="map-tab-head">
              <span>
                {mapItems.length} {mapItems.length === 1 ? 'lead' : 'leads'} en el mapa
                <span className="map-tab-hint"> · clic en un grupo para acercar, en un negocio para ver su ficha</span>
              </span>
              <div className="map-legend" style={{ margin: 0 }}>
                <span className="lg high"><i />Alta oport.</span>
                <span className="lg mid"><i />Media</span>
                <span className="lg low"><i />Baja</span>
              </div>
            </div>
            <BusinessMap items={mapItems} onOpenLead={setSelectedLead} fill />
          </div>
        ) : view === 'history' ? (
          <JobsHistory
            jobs={allJobs.jobs}
            total={allJobs.total}
            loading={allJobs.loading}
            loaded={allJobs.loaded}
            capped={allJobs.capped}
            error={allJobs.error}
            activeJobId={state.filters.job_id}
            onViewLeads={viewJobLeads}
            onResume={resumeJob}
          />
        ) : (
          <>
            <CommandBar
              zoneMode={zoneMode}
              singleSide={singleSide}
              onZoneMode={setZoneMode}
              onSingleSide={setSingleSide}
              onReset={resetWorkspace}
              sortByOpp={sortByOpp}
              onToggleSort={() => setSortByOpp(s => !s)}
              quota={quota}
            />

            {zoneMode === 'none' ? (
              <CommandBarMode state={state} onOpenLead={setSelectedLead} sortByOpp={sortByOpp} onAddToPipeline={addToPipelineNuevo} />
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={collisionDetectionStrategy}
                measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
              >
                <PanelsLayout
                  zoneMode={zoneMode}
                  singleSide={singleSide}
                  leftCollapsed={leftCollapsed}
                  rightCollapsed={rightCollapsed}
                  widgets={widgets}
                  onToggleCollapsed={toggleCollapsed}
                  renderWidgetCards={renderWidgetCards}
                  state={state}
                  onOpenLead={setSelectedLead}
                  sortByOpp={sortByOpp}
                  onAddToPipeline={addToPipelineNuevo}
                />
                <DragOverlay>
                  {activeDef ? <WidgetDragPreview def={activeDef} /> : null}
                </DragOverlay>
              </DndContext>
            )}
          </>
        )}
      </div>

      <LeadSlideOver
        lead={selectedLead}
        card={selectedLead ? pipeline.cardOf(selectedLead.place_id) : null}
        onClose={() => setSelectedLead(null)}
        pipelineStage={selectedLead ? pipeline.stageOf(selectedLead.place_id) : null}
        onAddToPipeline={selectedLead ? () => pipeline.add([selectedLead.place_id], 'nuevo') : undefined}
        onContact={selectedLead ? () => pipeline.add([selectedLead.place_id], 'contactado') : undefined}
        onSaveNextStep={selectedLead ? (next) => pipeline.setNextStep(selectedLead.place_id, next) : undefined}
      />
      <ConfirmModal state={state} />

      {/* T-028 — Modal de nueva extracción (acceso adicional al del sidebar).
          Reutiliza NewSearchForm; respeta el tema (chrome con tokens + form
          themed por `.cabina-form`). */}
      <div
        className={'extract-modal-wrap' + (showExtract ? ' open' : '')}
        aria-hidden={!showExtract}
        role="dialog"
        aria-modal="true"
        onClick={() => setShowExtract(false)}
      >
        <div className="extract-modal" onClick={(e) => e.stopPropagation()}>
          <div className="em-head">
            <div>
              <h3>Nueva extracción</h3>
              <p>Elige categoría y zona, estima el coste y lanza la extracción.</p>
            </div>
            <button className="so-close" onClick={() => setShowExtract(false)} aria-label="Cerrar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
          <div className="em-body">
            <NewSearchForm state={state} dropdownZIndex={90} />
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Disposición con laterales (modos "3 zonas" y "2 zonas")
// ============================================================================

/** True cuando el viewport es estrecho (tablet/móvil): apila el escritorio. */
function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 980px)').matches,
  )
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 980px)')
    const onChange = () => setNarrow(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return narrow
}

function PanelsLayout({
  zoneMode,
  singleSide,
  leftCollapsed,
  rightCollapsed,
  widgets,
  onToggleCollapsed,
  renderWidgetCards,
  state,
  onOpenLead,
  sortByOpp,
  onAddToPipeline,
}: {
  zoneMode: 'three' | 'two'
  singleSide: PanelSide
  leftCollapsed: boolean
  rightCollapsed: boolean
  widgets: WidgetState[]
  onToggleCollapsed: (side: PanelSide) => void
  renderWidgetCards: (list: WidgetState[], dropId: string) => ReactNode
  state: UseSearchAndInbox
  onOpenLead: (item: InboxItem) => void
  sortByOpp: boolean
  onAddToPipeline: (ids: string[]) => Promise<number> | void
}) {
  const narrow = useNarrow()
  if (zoneMode === 'two') {
    // Lateral único con TODOS los widgets combinados.
    const list = allWidgetsSorted(widgets)
    const collapsed = singleSide === 'left' ? leftCollapsed : rightCollapsed
    const colWidth = collapsed ? RAIL_WIDTH : PANEL_WIDTH.single
    const gridCols =
      singleSide === 'left'
        ? `${colWidth} minmax(0,1fr)`
        : `minmax(0,1fr) ${colWidth}`

    const panel = collapsed ? (
      <CollapsedRail
        side={singleSide}
        widgetList={list}
        onExpand={() => onToggleCollapsed(singleSide)}
      />
    ) : (
      <SidePanel
        side={singleSide}
        onCollapse={() => onToggleCollapsed(singleSide)}
      >
        {renderWidgetCards(list, DROP_ID[singleSide])}
      </SidePanel>
    )

    return (
      <div className="cab-desk grid gap-4" style={{ gridTemplateColumns: narrow ? '1fr' : gridCols }}>
        {singleSide === 'left' && panel}
        <InboxCenter state={state} showControls onOpenLead={onOpenLead} sortByOpp={sortByOpp} onAddToPipeline={onAddToPipeline} />
        {singleSide === 'right' && panel}
      </div>
    )
  }

  // 3 zonas
  const leftList = widgetsInPanel(widgets, 'left')
  const rightList = widgetsInPanel(widgets, 'right')
  const gridCols = `${leftCollapsed ? RAIL_WIDTH : PANEL_WIDTH.left} minmax(0,1fr) ${
    rightCollapsed ? RAIL_WIDTH : PANEL_WIDTH.right
  }`

  return (
    <div className="cab-desk grid gap-4" style={{ gridTemplateColumns: narrow ? '1fr' : gridCols }}>
      {leftCollapsed ? (
        <CollapsedRail
          side="left"
          widgetList={leftList}
          onExpand={() => onToggleCollapsed('left')}
        />
      ) : (
        <SidePanel side="left" onCollapse={() => onToggleCollapsed('left')}>
          {renderWidgetCards(leftList, DROP_ID.left)}
        </SidePanel>
      )}

      <InboxCenter state={state} showControls onOpenLead={onOpenLead} sortByOpp={sortByOpp} onAddToPipeline={onAddToPipeline} />

      {rightCollapsed ? (
        <CollapsedRail
          side="right"
          widgetList={rightList}
          onExpand={() => onToggleCollapsed('right')}
        />
      ) : (
        <SidePanel side="right" onCollapse={() => onToggleCollapsed('right')}>
          {renderWidgetCards(rightList, DROP_ID.right)}
        </SidePanel>
      )}
    </div>
  )
}

function SidePanel({
  side,
  onCollapse,
  children,
}: {
  side: PanelSide
  onCollapse: () => void
  children: ReactNode
}) {
  return (
    <div className="min-w-0 space-y-3">
      <div className="flex items-center justify-between px-0.5">
        <span className="panel-label" style={{ margin: 0 }}>
          {side === 'left' ? 'Lateral izquierdo' : 'Lateral derecho'}
        </span>
        <button
          type="button"
          onClick={onCollapse}
          title="Colapsar lateral"
          className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-blue-600"
        >
          {side === 'left' ? (
            <PanelLeftClose size={14} />
          ) : (
            <PanelRightClose size={14} />
          )}
        </button>
      </div>
      {children}
    </div>
  )
}

/**
 * Raíl compacto al colapsar un lateral. Detalle distintivo (DEC-015): en
 * lugar de un sidebar estándar, queda un raíl estrecho con los iconos de
 * los widgets que contiene, label vertical y acento azul al hover.
 */
function CollapsedRail({
  side,
  widgetList,
  onExpand,
}: {
  side: PanelSide
  widgetList: WidgetState[]
  onExpand: () => void
}) {
  return (
    <button
      type="button"
      onClick={onExpand}
      title={`Expandir lateral ${side === 'left' ? 'izquierdo' : 'derecho'}`}
      className="group flex h-full min-h-[160px] w-12 flex-col items-center gap-2.5 rounded-xl border border-gray-200 bg-gradient-to-b from-white to-gray-50 py-3 text-gray-400 shadow-sm transition-colors hover:border-blue-300 hover:text-blue-600 hover:shadow"
    >
      <span className="rounded-lg bg-gray-100 p-1 transition-colors group-hover:bg-blue-100 group-hover:text-blue-600">
        {side === 'left' ? (
          <ChevronRight size={14} />
        ) : (
          <ChevronLeft size={14} />
        )}
      </span>
      <span className="h-px w-5 bg-gray-200" />
      {widgetList.map(w => {
        const def = WIDGET_REGISTRY.find(d => d.id === w.id)!
        const Icon = def.Icon
        return (
          <span
            key={w.id}
            title={def.label}
            className="rounded-lg bg-white p-1.5 shadow-sm ring-1 ring-gray-100 transition-colors group-hover:ring-blue-100"
          >
            <Icon
              size={14}
              className="text-gray-500 transition-colors group-hover:text-blue-600"
            />
          </span>
        )
      })}
      <span className="mt-auto rotate-180 text-[10px] font-semibold uppercase tracking-wider text-gray-400 [writing-mode:vertical-rl] group-hover:text-blue-500">
        {side === 'left' ? 'Izquierda' : 'Derecha'}
      </span>
    </button>
  )
}

function EmptyPanelHint() {
  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/60 p-4 text-center text-[11px] leading-relaxed text-gray-500">
      Este lateral está vacío. Arrastra un widget aquí, o usa los botones{' '}
      <span className="inline-flex items-center gap-0.5 font-medium text-gray-700">
        <ChevronLeft size={11} /> / <ChevronRight size={11} />
      </span>{' '}
      en la cabecera de un widget.
    </div>
  )
}

// ============================================================================
// Lista sortable de un lateral (área droppable + contexto sortable dnd-kit)
// ============================================================================

interface WidgetCardCommonProps {
  def: WidgetDef
  body: ReactNode
  showPanelMove: boolean
  currentPanel: PanelSide
  canMoveUp: boolean
  canMoveDown: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  onSwitchPanel: () => void
}

function SortableWidgetList({
  list,
  dropId,
  showPanelMove,
  state,
  onMoveUp,
  onMoveDown,
  onSwitchPanel,
}: {
  list: WidgetState[]
  dropId: string
  showPanelMove: boolean
  state: UseSearchAndInbox
  onMoveUp: (id: WidgetId) => void
  onMoveDown: (id: WidgetId) => void
  onSwitchPanel: (id: WidgetId, panel: PanelSide) => void
}) {
  const { setNodeRef, isOver } = useDroppable({ id: dropId })
  return (
    <div
      ref={setNodeRef}
      className={
        'space-y-3 rounded-lg transition-colors ' +
        (isOver ? 'bg-blue-50/50 ring-2 ring-blue-300 ring-offset-2' : '')
      }
    >
      <SortableContext
        items={list.map(w => w.id)}
        strategy={verticalListSortingStrategy}
      >
        {list.length === 0 ? (
          <EmptyPanelHint />
        ) : (
          list.map((w, i) => {
            const def = WIDGET_REGISTRY.find(d => d.id === w.id)!
            return (
              <SortableWidgetCard
                key={w.id}
                def={def}
                body={def.render(state)}
                showPanelMove={showPanelMove}
                currentPanel={w.panel}
                canMoveUp={i > 0}
                canMoveDown={i < list.length - 1}
                onMoveUp={() => onMoveUp(w.id)}
                onMoveDown={() => onMoveDown(w.id)}
                onSwitchPanel={() => onSwitchPanel(w.id, w.panel)}
              />
            )
          })
        )}
      </SortableContext>
    </div>
  )
}

function SortableWidgetCard(props: WidgetCardCommonProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.def.id })

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const dragHandle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      title="Arrastra para mover el widget (o usa las flechas)"
      aria-label="Arrastrar widget"
      className="cursor-grab touch-none rounded p-0.5 text-gray-300 transition-colors hover:bg-gray-100 hover:text-gray-500 active:cursor-grabbing"
    >
      <GripVertical size={12} />
    </button>
  )

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={isDragging ? 'opacity-40' : undefined}
    >
      <WidgetCard {...props} dragHandle={dragHandle} />
    </div>
  )
}

// ============================================================================
// Tarjeta de widget: cabecera (grip de arrastre + controles ←/→ ↑/↓) + cuerpo
// ============================================================================

function WidgetCard({
  def,
  body,
  showPanelMove,
  currentPanel,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onSwitchPanel,
  dragHandle,
}: WidgetCardCommonProps & { dragHandle?: ReactNode }) {
  const Icon = def.Icon
  return (
    <section className="widget">
      <header>
        <span className="w-title">
          {dragHandle ?? <span className="grip"><GripVertical size={12} /></span>}
          <Icon size={14} className="h" />
          {def.label}
        </span>
        <div className="ctrls">
          {showPanelMove && (
            <WidgetCtrlButton
              title={
                currentPanel === 'left'
                  ? 'Mover al lateral derecho'
                  : 'Mover al lateral izquierdo'
              }
              onClick={onSwitchPanel}
            >
              {currentPanel === 'left' ? (
                <ChevronRight size={13} />
              ) : (
                <ChevronLeft size={13} />
              )}
            </WidgetCtrlButton>
          )}
          <WidgetCtrlButton title="Subir" onClick={onMoveUp} disabled={!canMoveUp}>
            <ChevronUp size={13} />
          </WidgetCtrlButton>
          <WidgetCtrlButton
            title="Bajar"
            onClick={onMoveDown}
            disabled={!canMoveDown}
          >
            <ChevronDown size={13} />
          </WidgetCtrlButton>
        </div>
      </header>
      <div className="body">{body}</div>
    </section>
  )
}

function WidgetCtrlButton({
  title,
  onClick,
  disabled = false,
  children,
}: {
  title: string
  onClick: () => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
    >
      {children}
    </button>
  )
}

/** Vista "elevada" que sigue al cursor mientras se arrastra un widget
 *  (DragOverlay de dnd-kit). Muestra solo la cabecera para no duplicar el
 *  cuerpo (formularios) del widget durante el arrastre. */
function WidgetDragPreview({ def }: { def: WidgetDef }) {
  const Icon = def.Icon
  return (
    <div className="flex items-center gap-1.5 rounded-lg border border-blue-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-lg ring-2 ring-blue-200">
      <GripVertical size={12} className="text-gray-400" />
      <Icon size={13} className="text-blue-600" />
      {def.label}
    </div>
  )
}

// ============================================================================
// Selector de modo de zonas
// ============================================================================

function CommandBar({
  zoneMode,
  singleSide,
  onZoneMode,
  onSingleSide,
  onReset,
  sortByOpp,
  onToggleSort,
  quota,
}: {
  zoneMode: ZoneMode
  singleSide: PanelSide
  onZoneMode: (m: ZoneMode) => void
  onSingleSide: (s: PanelSide) => void
  onReset: () => void
  sortByOpp: boolean
  onToggleSort: () => void
  quota: AccountQuota | null
}) {
  const options: { id: ZoneMode; label: string; desc: string; Icon: IconType }[] =
    [
      { id: 'three', label: '3 zonas', desc: 'Dos laterales + tabla central', Icon: LayoutGrid },
      { id: 'two', label: '2 zonas', desc: 'Un lateral + tabla', Icon: PanelLeft },
      { id: 'none', label: 'Sin laterales', desc: 'Solo la tabla a todo el ancho', Icon: Square },
    ]
  const planName =
    quota ? ({ free: 'Free', starter: 'Starter', pro: 'Pro' }[quota.plan_code] ?? quota.plan_code) : ''
  const used = quota ? quota.monthly_leads_consumed : 0
  const total = quota ? quota.monthly_leads_quota : 1
  const pct = Math.min(100, Math.round((used / Math.max(1, total)) * 100))

  return (
    <div className="command">
      <div className="seg">
        {options.map(o => {
          const Icon = o.Icon
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onZoneMode(o.id)}
              title={o.desc}
              className={o.id === zoneMode ? 'active' : ''}
            >
              <Icon size={14} />
              {o.label}
            </button>
          )
        })}
      </div>

      {zoneMode === 'two' && (
        <div className="seg">
          {(['left', 'right'] as const).map(side => (
            <button
              key={side}
              type="button"
              onClick={() => onSingleSide(side)}
              className={singleSide === side ? 'active' : ''}
            >
              {side === 'left' ? 'Izquierda' : 'Derecha'}
            </button>
          ))}
        </div>
      )}

      <button
        type="button"
        className={'sort' + (sortByOpp ? ' on' : '')}
        onClick={onToggleSort}
        title="Ordenar la tabla por Opportunity Score"
      >
        <ArrowDownWideNarrow size={14} />
        Ordenar por <b>{sortByOpp ? 'Oportunidad ↓' : 'Por defecto'}</b>
      </button>

      <button
        type="button"
        onClick={onReset}
        title="Restablecer la disposición"
        className="sort"
      >
        <RotateCcw size={12} />
        Restablecer
      </button>

      {quota && (
        <div className="quota-chip">
          Cupo {planName} <b>{used}/{total}</b>
          <span className="mini"><i style={{ width: `${pct}%` }} /></span>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Centro: bandeja (tabla + paginación). Compartido por todos los modos.
// ============================================================================

function InboxCenter({
  state,
  showControls,
  onOpenLead,
  sortByOpp,
  onAddToPipeline,
}: {
  state: UseSearchAndInbox
  showControls: boolean
  onOpenLead: (item: InboxItem) => void
  sortByOpp: boolean
  /** Añade los place_ids seleccionados al pipeline (stage 'nuevo'). */
  onAddToPipeline?: (ids: string[]) => Promise<number> | void
}) {
  const inboxHasFilters = hasActiveFilters(state.filters)
  const highCount = state.items.filter(i => opportunityScore(i).band === 'high').length
  const contactTop = () => {
    if (state.items.length === 0) return
    const top = [...state.items].sort(
      (a, b) => opportunityScore(b).score - opportunityScore(a).score,
    )[0]
    onOpenLead(top)
  }

  // Selección múltiple para "Añadir al pipeline" (solo si hay handler).
  const selectable = !!onAddToPipeline
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [adding, setAdding] = useState(false)
  // Al cambiar de página/filtros, limpiar la selección (place_ids ya no visibles).
  const pageKey = state.offset + '|' + state.items.length + '|' + (state.filters.job_id ?? '')
  useEffect(() => { setSelected(new Set()) }, [pageKey])
  const toggleSelect = (id: string) =>
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  const toggleAll = () =>
    setSelected(s => (state.items.every(i => s.has(i.place_id)) ? new Set() : new Set(state.items.map(i => i.place_id))))
  const addSelected = async () => {
    if (selected.size === 0 || !onAddToPipeline) return
    setAdding(true)
    try {
      await onAddToPipeline([...selected])
      setSelected(new Set())
    } finally {
      setAdding(false)
    }
  }

  // BUSCADOR de la página (multicampo `q`). Comparte el debounce del motor
  // ({@link useDebouncedValue}) y dirige el controlador de la bandeja (mismo `q`,
  // sin duplicar el fetch). `lastAppliedRef` evita pisar lo que el usuario teclea
  // cuando `filters.q` se actualiza por nuestra propia aplicación.
  const [searchInput, setSearchInput] = useState(
    state.filters.q ??
      (IS_PREVIEW ? new URLSearchParams(window.location.search).get('q') ?? '' : ''),
  )
  const debouncedSearch = useDebouncedValue(searchInput, 280)
  const lastAppliedRef = useRef(state.filters.q ?? '')
  useEffect(() => {
    const next = debouncedSearch.trim()
    const current = state.filters.q ?? ''
    if (next === current) return
    lastAppliedRef.current = next
    state.applyFilters({ ...state.filters, q: next || undefined })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])
  useEffect(() => {
    // Cambio EXTERNO de `q` (p. ej. "Limpiar filtros") → sincroniza el campo.
    const ext = state.filters.q ?? ''
    if (ext !== lastAppliedRef.current) {
      lastAppliedRef.current = ext
      setSearchInput(ext)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filters.q])
  const searching =
    searchInput.trim() !== debouncedSearch ||
    (state.loadingInbox && searchInput.trim() !== '')

  return (
    <div className="min-w-0">
      <div className="lead-search">
        <svg className="ls-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
        <input
          className="ls-input"
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Buscar por nombre, categoría, ciudad…"
          aria-label="Buscar leads en la bandeja"
        />
        {searching && <span className="ls-spin" aria-label="Buscando" />}
        {searchInput && (
          <button
            type="button"
            className="ls-clear"
            onClick={() => setSearchInput('')}
            aria-label="Limpiar búsqueda"
            title="Limpiar"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        )}
      </div>
      <div className="inbox-head">
        <h2>
          Mis leads
          <span className="c">
            · {state.totalItems.toLocaleString('es-ES')}
            {inboxHasFilters ? ' filtrados' : ' totales'}
            {state.inboxSort.by
              ? ` · ordenados por ${INBOX_SORT_LABELS[state.inboxSort.by]} ${state.inboxSort.dir === 'asc' ? '↑' : '↓'}`
              : sortByOpp ? ' · ordenados por oportunidad' : ''}
          </span>
        </h2>
        {showControls && (
          <div className="toolbar">
            <button
              type="button"
              className="btn btn-accent btn-sm"
              onClick={contactTop}
              disabled={state.items.length === 0}
              title="Abrir la ficha de contacto del lead de mayor oportunidad"
            >
              <Send size={14} />
              Contactar{highCount > 0 ? ` (${highCount})` : ''}
            </button>
            <ColumnPickerButton
              visibleCols={state.visibleCols}
              toggleCol={state.toggleCol}
              resetCols={state.resetCols}
              showAllCols={state.showAllCols}
            />
            <ExportButtons
              exportLoading={state.exportLoading}
              itemsCount={state.items.length}
              hasJobFilter={!!state.filters.job_id}
              onExport={state.handleExportInbox}
            />
          </div>
        )}
      </div>

      <InboxErrorBanner message={state.inboxError} />

      {selectable && selected.size > 0 && (
        <div className="bulk-bar">
          <span className="bulk-n">{selected.size} seleccionado{selected.size === 1 ? '' : 's'}</span>
          <button type="button" className="btn btn-accent btn-sm" onClick={addSelected} disabled={adding}>
            <Plus size={14} />{adding ? 'Añadiendo…' : 'Añadir al pipeline'}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>Limpiar</button>
        </div>
      )}

      {state.items.length === 0 ? (
        <InboxEmptyState
          hasFilters={inboxHasFilters}
          loading={state.loadingInbox && state.items.length === 0}
        />
      ) : (
        <>
          <InboxTable
            items={state.items}
            visibleCols={state.visibleCols}
            onOpenLead={onOpenLead}
            sortByOpportunity={sortByOpp}
            activeSort={state.inboxSort}
            onSort={state.setInboxColumnSort}
            selectable={selectable}
            selectedIds={selected}
            onToggleSelect={toggleSelect}
            onToggleAll={toggleAll}
          />
          <Paginator
            offset={state.offset}
            itemsLength={state.items.length}
            totalItems={state.totalItems}
            pageSize={state.pageSize}
            loading={state.loadingInbox}
            onPrev={() =>
              state.loadInbox(
                Math.max(0, state.offset - state.pageSize),
                state.filters,
              )
            }
            onNext={() =>
              state.loadInbox(state.offset + state.pageSize, state.filters)
            }
          />
        </>
      )}
    </div>
  )
}

// ============================================================================
// Modo "sin laterales": command bar compacta arriba + tabla full-width.
// ============================================================================

function CommandBarMode({
  state,
  onOpenLead,
  sortByOpp,
  onAddToPipeline,
}: {
  state: UseSearchAndInbox
  onOpenLead: (item: InboxItem) => void
  sortByOpp: boolean
  onAddToPipeline: (ids: string[]) => Promise<number> | void
}) {
  const [newSearchOpen, setNewSearchOpen] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const newSearchRef = useRef<HTMLDivElement | null>(null)
  const filtersRef = useRef<HTMLDivElement | null>(null)

  const filtersCount = countActiveFilters(state.filters)

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        newSearchOpen &&
        newSearchRef.current &&
        !newSearchRef.current.contains(target)
      ) {
        setNewSearchOpen(false)
      }
      if (
        filtersOpen &&
        filtersRef.current &&
        !filtersRef.current.contains(target)
      ) {
        setFiltersOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [newSearchOpen, filtersOpen])

  const removeFilter = (k: keyof InboxFilters) =>
    state.applyFilters({ ...state.filters, [k]: undefined })

  return (
    <>
      <div className="mb-4 rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-2 px-3 py-2">
          {/* + Nueva extracción */}
          <div className="relative" ref={newSearchRef}>
            <button
              type="button"
              onClick={() => {
                setNewSearchOpen(o => !o)
                setFiltersOpen(false)
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
            >
              <Plus size={12} />
              Nueva extracción
            </button>
            {newSearchOpen && (
              <div className="absolute left-0 top-full z-30 mt-1 w-[560px] max-w-[90vw] rounded-md border border-gray-200 bg-white p-4 shadow-xl">
                <NewSearchForm state={state} compact />
              </div>
            )}
          </div>

          {/* El buscador multicampo `q` vive ahora como campo prominente en la
              bandeja (InboxCenter). Aquí solo quedan los filtros estructurales. */}

          {/* Selector búsqueda concreta */}
          <select
            value={state.filters.job_id ?? ''}
            onChange={e =>
              state.applyFilters({
                ...state.filters,
                job_id: e.target.value || undefined,
              })
            }
            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs focus:border-blue-400 focus:outline-none"
            title="Filtrar por una búsqueda concreta"
          >
            <option value="">Todas mis búsquedas ({state.jobs.length})</option>
            {state.jobs.slice(0, 100).map(j => (
              <option key={j.id} value={j.id}>
                {jobLabel(j)}
              </option>
            ))}
          </select>

          {/* Filtros avanzados (popover) */}
          <div className="relative" ref={filtersRef}>
            <button
              type="button"
              onClick={() => {
                setFiltersOpen(o => !o)
                setNewSearchOpen(false)
              }}
              className={
                'inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs font-medium ' +
                (filtersCount > 0
                  ? 'border-blue-300 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50')
              }
            >
              <Filter size={12} />
              Filtros{filtersCount > 0 ? ` · ${filtersCount}` : ''}
            </button>
            {filtersOpen && (
              <div className="absolute right-0 top-full z-30 mt-1 w-80 max-w-[90vw] rounded-md border border-gray-200 bg-white p-4 shadow-xl">
                <FiltersPanel
                  filters={state.filters}
                  jobs={state.jobs}
                  onApply={f => {
                    state.applyFilters(f)
                    setFiltersOpen(false)
                  }}
                  onClear={() => {
                    state.clearFilters()
                    setFiltersOpen(false)
                  }}
                  bare
                />
              </div>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <ColumnPickerButton
              visibleCols={state.visibleCols}
              toggleCol={state.toggleCol}
              resetCols={state.resetCols}
              showAllCols={state.showAllCols}
            />
            <ExportButtons
              exportLoading={state.exportLoading}
              itemsCount={state.items.length}
              hasJobFilter={!!state.filters.job_id}
              onExport={state.handleExportInbox}
            />
          </div>
        </div>

        {filtersCount > 0 && (
          <div className="border-t border-gray-100 px-3 py-2">
            <FiltersInlineChips
              filters={state.filters}
              jobs={state.jobs}
              onClear={state.clearFilters}
              onRemove={removeFilter}
            />
          </div>
        )}
      </div>

      <InboxCenter state={state} showControls={false} onOpenLead={onOpenLead} sortByOpp={sortByOpp} onAddToPipeline={onAddToPipeline} />
    </>
  )
}
